import React, { useState, useEffect } from "react";
import {
  Upload,
  FileText,
  User,
  Briefcase,
  MapPin,
  Calendar,
  DollarSign,
  CheckCircle,
  Truck,
  X,
  Loader2,
  Sparkles,
  ChevronRight,
  PenTool,
  AlertCircle,
  Bug,
} from "lucide-react";
import { Form } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  useAddJobMutation,
  useEditJobMutation,
} from "../../redux/services/JobService";
import { useGetSelectFactoryCompanyQuery } from "../../redux/services/FactoryCompanyService";
import { useGetSelectBrokersQuery } from "../../redux/services/BrokerService";
import { useGetSelectDriversQuery } from "../../redux/services/DriverService";
import PickUpLocation from "./PickUpLocation";
import DropOffLocation from "./DropOffLocation";
import LoadingButtonWithForm from "../../components/LoadingButtonWithForm";
import useAIRateConfirmation from "../../hooks/useAIRateConfirmation";
import { error } from "pdf-lib";
import moment from "moment";
import { convertToFormDataDeepCopy, sweetAlert } from "../../utils";
import { useNavigate } from "react-router-dom";
import FilePreview from "./components/FilePreview";

export default function AddJob({ isEdit }) {
  const navigate = useNavigate();
  let {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const { documentHandler, ProgressBar, aiJsonResponse } =
    useAIRateConfirmation();

  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dataExtracted, setDataExtracted] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [extractionError, setExtractionError] = useState(false);
  const [showAdditionalPick, setShowAdditionalPick] = useState(false);
  const [showAdditionalDrop, setShowAdditionalDrop] = useState(false);

  const [addRequest, { isLoading: addLoading }] = useAddJobMutation();
  const [editRequest, { isLoading: editLoading }] = useEditJobMutation();

  const { data: data2 } = useGetSelectDriversQuery();
  const drivers = data2?.response?.data;
  const { data: data3 } = useGetSelectBrokersQuery();
  const brokers = data3?.response?.data;
  const { data: data4 } = useGetSelectFactoryCompanyQuery();
  const factoryCompany = data4?.response?.data;

  // Form State
  const [formData, setFormData] = useState({
    driver: "",
    factoringCompany: "",
    jobRef: "Trip-765441",
    pickupLocation: "",
    pickupDate: "",
    dropoffLocation: "",
    dropoffDate: "",
    amount: "",
    miles: "",
    ratePerMile: "",
    showOriginalDoc: true,
    showRateToDriver: true,
  });

  // Mock Drivers/Companies
  // const drivers = ["John Doe", "Sarah Smith", "Mike Ross"];
  const companies = [
    "McMillan Davis Associates",
    "QuickPay Logistics",
    "BlueStar Factoring",
  ];

  // Handle Drag & Drop
  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const generateJobRef = () => {
    return `Trip-${Math.floor(100000 + Math.random() * 900000)}`;
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    onRateConfirmationHandler(droppedFile);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onRateConfirmationHandler(e.target.files[0]);
    }
  };

  const handleFileUpload = (uploadedFile) => {
    setFile(uploadedFile);
    setIsProcessing(true);
    setExtractionError(false);

    // Simulate AI Extraction Logic with Random Error Chance
    setTimeout(() => {
      setIsProcessing(false);

      // For demo purposes: 10% chance of "error" to show the fallback UI
      // In production, this would be real API error handling
      const isError = Math.random() < 0.1;

      if (isError) {
        setExtractionError(true);
        setManualMode(true); // Auto-open manual mode on error
        setDataExtracted(false);
      } else {
        setDataExtracted(true);
        setManualMode(false);
        // Simulate Extracted Data
        setFormData((prev) => ({
          ...prev,
          pickupLocation:
            "Distribution Center A, 123 Logistics Way, Atlanta, GA",
          pickupDate: "2023-10-24T08:00",
          dropoffLocation: "Walmart DC #44, 456 Commerce Blvd, Savannah, GA",
          dropoffDate: "2023-10-25T14:00",
          amount: "1250.00",
          miles: "248",
          ratePerMile: "5.04",
        }));
      }
    }, 2000);
  };

  const simulateError = () => {
    // specific function to demonstrate error handling
    const dummyFile = { name: "corrupted_scan.png" };
    setFile(dummyFile);
    setIsProcessing(true);
    setExtractionError(false);

    setTimeout(() => {
      setIsProcessing(false);
      setExtractionError(true);
      setManualMode(true);
      setDataExtracted(false);
    }, 2000);
  };

  const enableManualMode = () => {
    setManualMode(true);
    setExtractionError(false);
  };

  const onSubmit = async (data) => {
    data["job_title"] = generateJobRef();
    data["new_module"] = "yes";
    data["pickup_place_name"] = "hy";
    data["drop_place_name"] = "bro";
    data["job_amount"] = Number(data?.job_amount);
    data["job_start_datetime"] = moment(data?.job_start_datetime).format(
      "YYYY-MM-DD H:mm:ss",
    );
    data["drop_datetime"] = moment(data?.drop_datetime).format(
      "YYYY-MM-DD H:mm:ss",
    );
    data["show_original_document"] = data?.show_original_document
      ? data?.show_original_document
      : 0;
    data["show_rate_to_driver"] = data?.show_rate_to_driver
      ? data?.show_rate_to_driver
      : 0;
    // data["deleted_trip_docs"] =
    //   deletedTripDocs?.length > 0 ? deletedTripDocs : [];

    data["trip_documents"] = [];

    if (isEdit) {
      const newForm = convertToFormDataDeepCopy({ ...data });

      await editRequest({ id: isEdit?.id, newForm })
        .unwrap()
        .then((res) => {
          if (res?.status) {
            sweetAlert(res?.message, "success");
            // navigate(-1)
            // handleClose();
          }
        })
        .catch((err) => {
          responseError(err);
        });
    } else {
      const newForm = convertToFormDataDeepCopy({ ...data });

      await addRequest(newForm)
        .unwrap()
        .then((res) => {
          console.log("res", res);
          if (res?.status) {
            sweetAlert(res?.message, "success");
            navigate("/job");
            // handleClose();
          }
        })
        .catch((err) => {
          console.log("err", err);
          sweetAlert(err?.response?.message || "Something went wrong", "error");
        });
    }
  };

  const onRateConfirmationHandler = async (file) => {
    if (!file) return;

    // If Dropify returns FileList
    const selectedFile = file instanceof FileList ? file[0] : file;

    setFile(selectedFile);
    setIsProcessing(true);
    setExtractionError(false);
    // setRateDocName(selectedFile || "");

    const data = await documentHandler(file);
    if (data?.job_amount > 0) {
      setDataExtracted(true);
      setManualMode(false);
    } else {
      setExtractionError(true);
      setManualMode(true); // Auto-open manual mode on error
      setDataExtracted(false);
    }
    console.log("data", data);

    if (
      data?.pickup &&
      data?.pickup?.place_id &&
      data?.pickup?.lat &&
      data?.pickup?.lng
    ) {
      console.log("enter in pickup");
      console.log(
        data?.pickup?.place_id,
        data?.pickup?.address,
        data?.pickup?.lat,
        data?.pickup?.lng,
      );
      setValue("pickup_place_id", data?.pickup?.place_id);
      setValue("pickup_address", data?.pickup?.address);
      setValue("pickup_latitude", data?.pickup?.lat);
      setValue("pickup_longitude", data?.pickup?.lng);
    }

    if (
      data?.drop &&
      data?.drop?.place_id &&
      data?.drop?.lat &&
      data?.drop?.lng
    ) {
      console.log("enter in dropoff");
      console.log(
        data?.drop?.place_id,
        data?.drop?.address,
        data?.drop?.lat,
        data?.drop?.lng,
      );
      setValue("drop_place_id", data?.drop?.place_id);
      setValue("drop_address", data?.drop?.address);
      setValue("drop_latitude", data?.drop?.lat);
      setValue("drop_longitude", data?.drop?.lng);
    }

    setValue("job_start_datetime", data?.pickup?.datetime);
    setValue("drop_datetime", data?.drop?.datetime);

    setValue("job_amount", data?.job_amount || "");
    setValue("pickup_person_phone", data?.pickup?.contact_phone);
    setValue("pickup_person_name", data?.pickup?.contact_person);

    setValue("drop_person_phone", data?.drop?.contact_phone);
    setValue("drop_person_name", data?.drop?.contact_person);
  };

  const resetAll = () => {
    setFile(null);
    setDataExtracted(false);
    setManualMode(false);
    setExtractionError(false);
    setIsProcessing(false);
    setIsDragging(false);
    const defaultFormData = {
      driver: "",
      factoringCompany: "",
      jobRef: genreateJobRef(),
      pickupLocation: "",
      pickupDate: "",
      dropoffLocation: "",
      dropoffDate: "",
      amount: "",
      miles: "",
      ratePerMile: "",
      showOriginalDoc: true,
      showRateToDriver: true,
    };
    setFormData(defaultFormData);

    reset({
      driver: "",
      factoringCompany: "",
      jobRef: defaultFormData.jobRef,
      pickupLocation: "",
      pickupDate: "",
      dropoffLocation: "",
      dropoffDate: "",
      amount: "",
      miles: "",
      ratePerMile: "",
      showOriginalDoc: true,
      showRateToDriver: true,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleSetting = (setting) => {
    setFormData((prev) => ({ ...prev, [setting]: !prev[setting] }));
  };

  // Watch values for job amount and miles
  const formValues = useWatch({ control });
  const jobAmount = useWatch({ control, name: "job_amount" });
  const estimatedMiles = useWatch({ control, name: "estimated_miles" });
  useEffect(() => {
    // When both values exist and miles > 0, update the calculated field
    if (jobAmount && estimatedMiles > 0) {
      const calculatedValue =
        parseFloat(jobAmount) / parseFloat(estimatedMiles);
      setValue("estimated_dollar_per_mile", calculatedValue?.toFixed(2));
    } else {
      setValue("estimated_dollar_per_mile", "");
    }
  }, [jobAmount, estimatedMiles, control]);

  const isFormVisible = dataExtracted || manualMode;

  console.log({ formValues });
  // Validation Logic
  const isFormValid =
    formValues?.driver &&
    formValues?.factoring_companies_id &&
    formValues?.pickupLocation &&
    formValues?.pickupDate &&
    formValues?.dropoffLocation &&
    formValues?.dropoffDate &&
    formValues?.job_amount;

  const genreateJobRef = () => {
    return `Trip-${Math.floor(100000 + Math.random() * 900000)}`;
  };
  useEffect(() => {
    setFormData((prev) => ({ ...prev, jobRef: genreateJobRef() }));
  }, []);

  const handleGenerateClick = () => {
    setFormData((prev) => ({ ...prev, jobRef: genreateJobRef() }));
  };
  useEffect(() => {
    if (factoryCompany?.length > 0) {
      const defaultCompany =
        factoryCompany.find((com) => Number(com.is_default) === 1) ||
        factoryCompany[0];
      setValue("factoring_companies_id", defaultCompany.id.toString());
      setValue("factoring_company_email", defaultCompany.email);
      setValue("factoring_company_phone", defaultCompany.phone);
    }
  }, [factoryCompany, setValue]);

  const backPage = () => {
  navigate(-1);
};
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans testing_job">
      {/* Header */}
      <div
        className="page-header mb-4 bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between shadow-sm"
        style={{ zIndex: 20 }}
      >
        {/* Left Side */}
        <div className="d-flex align-items-center gap-3">
          <div className="bg-warning p-2 rounded">
            <Truck
              className="text-white"
              style={{ width: "24px", height: "24px" }}
            />
          </div>

          <div>
            <h1 className="h5 fw-bold text-dark mb-0">New Trip Entry</h1>
            <small className="text-muted fw-medium">
              Job Ref: {formData.jobRef || "No Trip Ref yet"}
            </small>
          </div>
        </div>

        {/* Right Side */}
        <div className="d-flex gap-2">
          <button
            onClick={backPage}
            className="btn btn-link text-secondary fw-medium text-decoration-none"
          >
            Back
          </button>
          <button
            onClick={resetAll}
            className="btn btn-link text-secondary fw-medium text-decoration-none"
          >
            Reset
          </button>

          <LoadingButtonWithForm
            isLoading={editLoading ? editLoading : addLoading}
            name="Save"
            form="job-add-form"
          />
          {/* <button
            disabled={!isFormVisible || !isFormValid}
            title={
              !isFormValid ? "Please fill in all required fields" : "Save Trip"
            }
            className={`btn btn-primary fw-bold d-flex align-items-center gap-2 px-4`}
          >
            Save Trip
          </button> */}
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="container-xl px-4 py-4"
        id="job-add-form"
      >
        {/* Step 1: Pre-requisites (Always Visible) */}
        <section className="bg-white rounded-2 shadow-sm border p-4 mb-4">
          <div className="row g-4">
            {/* Driver Select */}
            <div className="col-12 col-md-6">
              <div className="mb-2">
                <label className="form-label fw-semibold text-secondary d-flex align-items-center gap-1">
                  <User
                    className="text-warning"
                    style={{ width: "16px", height: "16px" }}
                  />
                  Select Driver <span className="text-danger">*</span>
                </label>
                <Controller
                  name="driver_id"
                  control={control}
                  defaultValue={isEdit?.driver_id || ""}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue(
                          "driver_email",
                          drivers?.find(
                            (driver) => driver?.id == e.target.value,
                          )?.email,
                        );
                        setValue(
                          "driver_phone",
                          drivers?.find(
                            (driver) => driver?.id == e.target.value,
                          )?.phone,
                        );
                      }}
                      className={`form-select py-2`}
                      isInvalid={errors.driver_id}
                    >
                      <option value="">Select Driver</option>
                      {drivers?.map((driver) => (
                        <>
                          <option value={driver?.id}>
                            {driver?.full_name}
                          </option>
                        </>
                      ))}
                    </Form.Select>
                  )}
                />
              </div>
            </div>

            {/* Factoring Company */}
            <div className="col-12 col-md-6">
              <div className="mb-2">
                <label className="form-label fw-semibold text-secondary d-flex align-items-center gap-1">
                  <Briefcase
                    className="text-warning"
                    style={{ width: "16px", height: "16px" }}
                  />
                  Factoring Company <span className="text-danger">*</span>
                </label>

                <Controller
                  name="factoring_companies_id"
                  control={control}
                  defaultValue={
                    factoryCompany
                      ?.find((com) => Number(com?.is_default) === 1)
                      ?.id?.toString() ||
                    factoryCompany?.[0]?.id?.toString() ||
                    ""
                  }
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        console.log(
                          "company",
                          factoryCompany?.find(
                            (driver) => driver?.id == e.target.value,
                          ),
                        );
                        setValue(
                          "factoring_company_email",
                          factoryCompany?.find(
                            (driver) => driver?.id == e.target.value,
                          )?.email,
                        );
                        setValue(
                          "factoring_company_phone",
                          factoryCompany?.find(
                            (driver) => driver?.id == e.target.value,
                          )?.phone,
                        );
                      }}
                      className={`form-select py-2`}
                      isInvalid={errors.factoring_companies_id}
                    >
                      <option value="">Select Company</option>
                      {factoryCompany?.map((item) => (
                        <>
                          <option value={item?.id}>{item?.company_name}</option>
                        </>
                      ))}
                    </Form.Select>
                  )}
                />
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* LEFT COLUMN: Data Entry / Review */}
          <div
            className={`flex-1 transition-all duration-500 ${isFormVisible ? "w-full lg:w-1/2" : "w-full"}`}
          >
            {/* Step 2: Upload Zone (Hidden/Collapsed logic) */}
            {!isFormVisible && (
              <div className="fade show">
                {/* Upload Box */}
                <div
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  className={`position-relative overflow-hidden rounded-2 border border-2 border-dashed p-5 text-center mb-4 cursor-pointer transition
    ${isDragging ? "border-warning bg-warning bg-opacity-10" : "border-secondary hover:border-warning bg-white"}
    ${isProcessing ? "bg-light" : ""}
    ${errors.rate_confirmation_document ? "border-danger" : ""}
  `}
                >
                  <Controller
                    name="rate_confirmation_document"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Please upload a POD or Rate Sheet" }}
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <>
                        <input
                          type="file"
                          className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                          onChange={(e) => {
                            handleFileChange(e);
                            onChange(e.target.files[0]); 
                          }}
                          disabled={isProcessing}
                        />
                        {/* Error Message */}
                        {error && (
                          <div className="text-danger mt-2 fw-semibold">
                            {error.message}
                          </div>
                        )}
                      </>
                    )}
                  />

                  {isProcessing ? (
                    <>
                      <ProgressBar />
                    </>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center py-3">
                      <div className="bg-primary bg-opacity-10 p-3 rounded-circle mb-3">
                        <Upload
                          className="text-white"
                          style={{ width: 32, height: 32 }}
                        />
                      </div>

                      <h4 className="fw-bold text-dark mb-2">
                        Upload POD or Rate Sheet
                      </h4>

                      <p
                        className="text-muted mx-auto mb-4"
                        style={{ maxWidth: 420 }}
                      >
                        Drag and drop your PDF or Image here. We will
                        automatically fill out the trip details for you.
                      </p>

                      <button className="btn btn-primary px-4 py-2 fw-medium shadow-sm">
                        Browse Files
                      </button>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="text-center position-relative my-4">
                  <hr />
                  <span className="position-absolute top-50 start-50 translate-middle bg-light px-2 text-muted small">
                    or
                  </span>
                </div>

                {/* Manual Entry */}
                <div className="text-center mt-4">
                  <button
                    onClick={enableManualMode}
                    className="btn btn-light border fw-semibold d-inline-flex align-items-center gap-2 px-4 py-3 shadow-sm"
                  >
                    <PenTool size={16} />
                    Enter Trip Details Manually
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Extracted Form Data (Reveals after upload OR manual click) */}
            {isFormVisible && (
              <div className="d-flex flex-column gap-4 fade show">
                {/* Success Message */}
                {dataExtracted && (
                  <div className="alert alert-success d-flex align-items-start gap-3 shadow-sm">
                    <Sparkles
                      className="text-success mt-1"
                      style={{ width: "20px", height: "20px" }}
                    />
                    <div>
                      <h6 className="fw-semibold mb-1">
                        Data Extracted Successfully
                      </h6>
                      <p className="mb-0 small">
                        Please review the details below. You can edit any field
                        if needed.
                      </p>
                    </div>
                  </div>
                )}

                {extractionError && (
                  <div className="alert alert-danger d-flex align-items-start gap-3 shadow-sm">
                    <AlertCircle
                      className="text-danger mt-1"
                      style={{ width: "20px", height: "20px" }}
                    />
                    <div>
                      <h6 className="fw-semibold mb-1">Extraction Failed</h6>
                      <p className="mb-0 small">
                        We couldn't read the document automatically. Please
                        enter the details manually below.
                      </p>
                    </div>
                  </div>
                )}

                {/* Route Timeline Card */}
                <div className="bg-white rounded-2x shadow-sm border overflow-hidden">
                  {/* Header */}
                  <div className="bg-light px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold text-secondary mb-0">Trip Route</h6>

                    <span className="badge bg-primary-subtle text-primary fw-medium small">
                      {dataExtracted ? "Auto-Filled" : "Manual Entry"}
                    </span>
                  </div>

                  <div className="p-4 position-relative w-100">
                    {/* Vertical Connector Line */}
                    <div
                      className="position-absolute bg-secondary opacity-25"
                      style={{
                        left: "44px",
                        top: "48px",
                        bottom: "48px",
                        width: "2px",
                      }}
                    />

                    {/* Pickup */}
                    <div
                      className="d-flex gap-3 mb-4  position-relative"
                      style={{ left: "10px" }}
                    >
                      <div className="flex-shrink-0">
                        <div
                          className="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center border border-4 border-white shadow-sm"
                          style={{ width: 40, height: 40 }}
                        >
                          <span className="fw-bold text-primary small">A</span>
                        </div>
                      </div>

                      <div className="flex-grow d-flex flex-column gap-3 w-100">
                        {/* Pickup Location */}
                        <div>
                          <label className="form-label text-uppercase fw-bold text-muted small">
                            Pick Up Location{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <div className="d-flex align-items-center gap-2">
                            <MapPin size={16} className="text-muted" />
                            <Controller
                              name="pickup_address"
                              control={control}
                              defaultValue={isEdit?.pickup_address || ""}
                              rules={{
                                required: true,
                              }}
                              render={({ field }) => (
                                <PickUpLocation
                                  onSave={(place) => {
                                    setValue(
                                      "pickup_place_id",
                                      place?.place_id,
                                    );
                                    setValue(
                                      "pickup_address",
                                      place?.formatted_address,
                                    );
                                    setValue(
                                      "pickup_latitude",
                                      place?.geometry?.location?.lat(),
                                    );
                                    setValue(
                                      "pickup_longitude",
                                      place?.geometry?.location?.lng(),
                                    );
                                  }}
                                  defaultValue={isEdit?.pickup_address || ""}
                                  error={errors?.pickup_address}
                                  field={field}
                                />
                              )}
                            />
                          </div>
                        </div>

                        {/* Pickup Date */}
                        <div className="d-flex align-items-center w-100 gap-5 w-100">
                          <div className="w-100">
                            <label className="form-label text-uppercase fw-bold text-muted small">
                              ETA <span className="text-danger">*</span>
                            </label>

                            <div className="d-flex align-items-center gap-2 w-100">
                              <Calendar size={16} className="text-muted" />
                              <Controller
                                name="job_start_datetime"
                                control={control}
                                defaultValue={isEdit?.job_start_datetime || ""}
                                rules={{
                                  required: true,
                                }}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    // onChange={(e) => field.onChange(moment(e.target.value)?.format('YYYY-MM-DD H:mm:ss'))}
                                    type="datetime-local"
                                    className={`form-control custome_border_in border-0 border-bottom rounded-0 px-0 small`}
                                    isInvalid={errors.job_start_datetime}
                                  />
                                )}
                              />
                            </div>
                          </div>
                          <div className="w-100">
                            <Form.Label>Additional Information:</Form.Label>
                            <Form.Check
                              type="switch"
                              id="additional-info-toggle-pick"
                              className="w-100"
                              checked={showAdditionalPick}
                              onChange={() =>
                                setShowAdditionalPick((prev) => !prev)
                              }
                            />
                          </div>
                        </div>

                        {showAdditionalPick && (
                          <div className="d-flex gap-3 align-items-center">
                            <div className="w-100">
                              <Form.Group className="mb-3">
                                <Form.Label>Contact Person Phone</Form.Label>
                                <Controller
                                  name="pickup_person_phone"
                                  control={control}
                                  defaultValue={""}
                                  rules={{ required: false }}
                                  render={({ field }) => (
                                    <div
                                      className={`${
                                        errors?.pickup_person_phone
                                          ? "border border-danger"
                                          : ""
                                      }`}
                                    >
                                      <PhoneInput
                                        {...field}
                                        country="us"
                                        enableAreaCodes={true}
                                        inputProps={{
                                          name: "pickup_person_phone",
                                          required: true,
                                          autoFocus: true,
                                        }}
                                      />
                                    </div>
                                  )}
                                />
                              </Form.Group>
                            </div>

                            <div className="w-100">
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  Contact Person Name (Optional)
                                </Form.Label>
                                <Controller
                                  name="pickup_person_name"
                                  control={control}
                                  defaultValue={""}
                                  rules={{ required: false }}
                                  render={({ field }) => (
                                    <Form.Control
                                      {...field}
                                      type="text"
                                      isInvalid={errors?.pickup_person_name}
                                    />
                                  )}
                                />
                              </Form.Group>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dropoff */}
                    <div
                      className="d-flex gap-3 position-relative"
                      style={{ left: "10px" }}
                    >
                      <div className="flex-shrink-0">
                        <div
                          className="rounded-circle bg-warning-subtle d-flex align-items-center justify-content-center border border-4 border-white shadow-sm"
                          style={{ width: 40, height: 40 }}
                        >
                          <span className="fw-bold text-warning small">B</span>
                        </div>
                      </div>

                      <div className="flex-grow d-flex flex-column gap-3 w-100">
                        {/* Dropoff Location */}
                        <div>
                          <label className="form-label text-uppercase fw-bold text-muted small">
                            Drop Off Location{" "}
                            <span className="text-danger">*</span>
                          </label>

                          <div className="d-flex align-items-center gap-2">
                            <MapPin size={16} className="text-muted" />

                            <Controller
                              name="drop_address"
                              control={control}
                              defaultValue={isEdit?.drop_address || ""}
                              rules={{
                                required: true,
                              }}
                              render={({ field }) => (
                                <DropOffLocation
                                  onSave={(place) => {
                                    setValue("drop_place_id", place?.place_id);
                                    setValue(
                                      "drop_address",
                                      place?.formatted_address,
                                    );
                                    setValue(
                                      "drop_latitude",
                                      place?.geometry?.location?.lat(),
                                    );
                                    setValue(
                                      "drop_longitude",
                                      place?.geometry?.location?.lng(),
                                    );
                                  }}
                                  defaultValue={isEdit?.drop_address || ""}
                                  error={errors.drop_address}
                                  field={field}
                                />
                              )}
                            />
                          </div>
                        </div>

                        <div className="d-flex align-items-center w-100 gap-5 w-100">
                          <div className="w-100">
                            <label className="form-label text-uppercase fw-bold text-muted small">
                              ETD <span className="text-danger">*</span>
                            </label>

                            <div className="d-flex align-items-center gap-2 w-100">
                              <Calendar size={16} className="text-muted" />
                              <Controller
                                name="drop_datetime"
                                control={control}
                                defaultValue={isEdit?.drop_datetime || ""}
                                rules={{ required: true }}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    type="datetime-local"
                                    className={`form-control custome_border_in border-0 border-bottom rounded-0 px-0 small`}
                                    isInvalid={errors.drop_datetime}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="w-100">
                            <Form.Label>Additional Information:</Form.Label>
                            <Form.Check
                              type="switch"
                              id="additional-info-toggle-drop"
                              // label="Additional Information"
                              checked={showAdditionalDrop}
                              onChange={() =>
                                setShowAdditionalDrop((prev) => !prev)
                              }
                            />
                          </div>
                        </div>
                        {showAdditionalDrop && (
                          <>
                            <div className="d-flex gap-3 align-items-center ">
                              <div className="w-100">
                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Contact Person Phone (Optional)
                                  </Form.Label>
                                  <Controller
                                    name="drop_person_phone"
                                    control={control}
                                    defaultValue={""}
                                    rules={{
                                      required: false,
                                    }}
                                    render={({ field }) => (
                                      <div
                                        className={`${
                                          errors?.drop_person_phone
                                            ? "border border-danger"
                                            : ""
                                        }`}
                                      >
                                        <PhoneInput
                                          {...field}
                                          country="us"
                                          enableAreaCodes={true}
                                          inputProps={{
                                            name: "drop_person_phone",
                                            required: true,
                                            autoFocus: true,
                                          }}
                                        />
                                      </div>
                                    )}
                                  />
                                </Form.Group>
                              </div>

                              <div className="w-100">
                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Contact Person Name (Optional)
                                  </Form.Label>
                                  <Controller
                                    name="drop_person_name"
                                    control={control}
                                    defaultValue={""}
                                    rules={{
                                      required: false,
                                    }}
                                    render={({ field }) => (
                                      <Form.Control
                                        {...field}
                                        type="text"
                                        isInvalid={errors.drop_person_name}
                                      />
                                    )}
                                  />
                                </Form.Group>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financials Card */}
                <div className="bg-white rounded-2x shadow-sm border overflow-hidden">
                  {/* Header */}
                  <div className="bg-light px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold text-secondary mb-0">Financials</h6>
                  </div>

                  {/* Content Grid */}
                  <div className="p-4 row g-3">
                    {/* Total Amount */}
                    <div className="col-12 col-md-4">
                      <label className="form-label text-uppercase fw-bold text-muted small d-flex align-items-center gap-1">
                        Total Amount <span className="text-danger">*</span>
                      </label>

                      <div className="position-relative mt-2 ">
                        <DollarSign
                          className="position-absolute top-50 start-0 translate-middle-y text-muted custome_dollar"
                          style={{
                            left: "10px !important",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        <Controller
                          name={"job_amount"}
                          control={control}
                          defaultValue={isEdit?.job_amount || ""}
                          rules={{
                            required: true,
                          }}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="number"
                              placeholder="Enter job amount"
                              className="ps-5 pe-3 py-2 fw-bold"
                              isInvalid={errors?.job_amount}
                              
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Estimated Miles */}
                    <div className="col-12 col-md-4">
                      <label className="form-label text-uppercase fw-bold text-muted small">
                        Est. Miles
                      </label>
                      <Controller
                        name={"estimated_miles"}
                        control={control}
                        defaultValue={isEdit?.estimated_miles || ""}
                        rules={{
                          required: false,
                        }}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            className="mt-2"
                            isInvalid={errors?.estimated_miles}
                          />
                        )}
                      />
                    </div>

                    {/* Rate / Mile */}
                    <div className="col-12 col-md-4">
                      <label className="form-label text-uppercase fw-bold text-muted small">
                        Rate / Mile
                      </label>

                      <div className="position-relative mt-2">
                        <DollarSign
                          className="position-absolute top-50 start-0 translate-middle-y text-muted custome_dollar"
                          style={{
                            left: "0.75rem",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        <Controller
                          name={"estimated_dollar_per_mile"}
                          control={control}
                          defaultValue={isEdit?.estimated_dollar_per_mile || ""}
                          rules={{
                            required: false,
                          }}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="number"
                              isInvalid={errors?.estimated_dollar_per_mile}
                              className="ps-5 pe-3 py-2 "
                              readOnly // make this read-only since it’s auto-calculated
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings Card - New Addition */}
                <div className="bg-white rounded-2x shadow-sm border overflow-hidden">
                  {/* Header */}
                  <div className="bg-light px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold text-black mb-0">
                      Rate Confirmation Document
                    </h6>
                  </div>

                  {/* Content */}
                  <div className="p-4 d-flex flex-column gap-3">
                    {/* Show Original Document */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <label className="form-label fw-semibold mb-1">
                          Show Original Document
                        </label>
                        <p className="small text-muted mb-0">
                          Allow driver to view the original uploaded file
                        </p>
                      </div>

                      <Controller
                        name={"show_original_document"}
                        control={control}
                        defaultValue={isEdit?.show_original_document || 1}
                        rules={{
                          required: false,
                        }}
                        render={({ field }) => (
                          <>
                            <div className="form-switch">
                              <input
                                {...field}
                                type="checkbox"
                                className="form-check-input"
                                defaultChecked={field.value}
                                checked={field.value}
                                //   disabled={/* true ya false based on condition */}
                                onChange={(e) =>
                                  field.onChange(e.target.checked ? 1 : 0)
                                }
                              />
                            </div>
                          </>
                        )}
                      />
                    </div>

                    {/* Show Rate to Driver */}
                    <div className="d-flex justify-content-between align-items-center border-top pt-3">
                      <div>
                        <label className="form-label fw-semibold mb-1">
                          Show Rate to Driver
                        </label>
                        <p className="small text-muted mb-0">
                          Display the total dollar amount to the driver
                        </p>
                      </div>

                      <div className="form-check form-switch">
                        <Controller
                          name={"show_rate_to_driver"}
                          control={control}
                          defaultValue={isEdit?.show_rate_to_driver || 1}
                          rules={{
                            required: false,
                          }}
                          render={({ field }) => (
                            <>
                              <div className="form-switch">
                                <input
                                  {...field}
                                  type="checkbox"
                                  className="form-check-input "
                                  defaultChecked={field.value}
                                  checked={field.value}
                                  onChange={(e) =>
                                    field.onChange(e.target.checked ? 1 : 0)
                                  }
                                />
                              </div>
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Document Viewer (Appears after upload or manual click) */}
          {isFormVisible && (
            <FilePreview
              file={file}
              onChange={(e) => {
                setDataExtracted(false);
                setDataExtracted(false);
                handleFileChange(e);
              }}
            />
          )}
        </div>
      </form>
    </div>
  );
}
